import React from 'react';
import { showCustomDialog } from '../DialogHelper';
import { LogoutWarningContent } from './components';
import { useIdleTimeout } from './useIdleTimeout';
/**
 * A custom React hook that monitors user inactivity and triggers a logout warning dialog before session timeout.
 * Automatically refreshes the session on user activity and handles logout flow if idle timeout is reached.
 *
 * @param {IdleMonitorParams} params - Configuration object for idle monitoring behavior.
 * @param {number} params.sessionTimeoutMs - Total time in milliseconds before session expires.
 * @param {number} params.sessionRefreshMs - Debounce time for refreshing the session after activity.
 * @param {number} [params.monitorDelayMs] - Optional delay for monitoring interval.
 * @param {number} [params.warningTimeoutMs] - Optional time before session expiration to show warning dialog.
 * @param {() => void} params.onSessionRefresh - Callback to refresh session activity.
 * @param {() => void} params.onLogoutClick - Callback when the logout button is clicked.
 * @param {() => void} params.onLoggedOutAcknowledge - Callback when logout warning is acknowledged after timeout.
 * @param {() => void} [params.onBeforeSessionExpiredDialogShow] - Optional callback before showing the session expiration dialog.
 *
 * @returns {void} This hook does not return a value.
 */
export function useIdleUserMonitor(params) {
    var _a;
    const WARNING_TIMEOUT_MS = 60 * 1000;
    useIdleTimeout({
        idleTimeoutMs: params.sessionTimeoutMs - ((_a = params.warningTimeoutMs) !== null && _a !== void 0 ? _a : WARNING_TIMEOUT_MS),
        debounceMs: params.sessionRefreshMs,
        monitorDelayMs: params.monitorDelayMs,
        onIdle: (idleForMs, onIdleCancel) => {
            showLogoutWarningDialog({
                onSessionRefresh: () => {
                    params.onSessionRefresh();
                    onIdleCancel();
                },
                idleForMs,
                onLoggedOutAcknowledge: params.onLoggedOutAcknowledge,
                onLogoutClick: params.onLogoutClick,
                sessionTimeoutMs: params.sessionTimeoutMs,
                onBeforeSessionExpiredDialogShow: params.onBeforeSessionExpiredDialogShow,
            });
        },
        onActive: () => params.onSessionRefresh(),
    });
}
function showLogoutWarningDialog(params) {
    showCustomDialog({
        content: (close) => (React.createElement(LogoutWarningContent, { onLoggedOutAcknowledge: () => {
                params.onLoggedOutAcknowledge();
                close();
            }, onSessionRefresh: () => {
                params.onSessionRefresh();
                close();
            }, onLogoutClick: () => {
                params.onLogoutClick();
                close();
            }, onBeforeSessionExpiredDialogShow: params.onBeforeSessionExpiredDialogShow, idleForMs: params.idleForMs, sessionTimeoutMs: params.sessionTimeoutMs })),
    });
}
